import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { State } from "./state";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface StateSlice {
  states: State[];
  formData: Partial<State>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: StateSlice = {
  states: [],
  formData: { state_name: "", status: "active" },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchStates = createAsyncThunk(
  "state/fetchStates",
  async (params?: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      const url = `/master/state${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await enhancedApiClient.get(url);
      if (isSuccessResponse(response)) {
        console.log("Fetch states response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch states");
      }
    } catch (error: any) {
      console.error("Fetch states error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch states";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create State
export const createState = createAsyncThunk(
  "state/createState",
  async (data: Partial<State>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/state", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create state");
      }
    } catch (error: any) {
      console.error("Create state error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create state";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit State
export const editState = createAsyncThunk(
  "state/editState",
  async ({ id, data }: { id: string; data: Partial<State> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/state/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update state");
      }
    } catch (error: any) {
      console.error("Edit state error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update state";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle state status
export const toggleStateStatus = createAsyncThunk(
  "state/toggleStateStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/state/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle state status");
      }
    } catch (error: any) {
      console.error("Toggle state status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle state status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const stateSlice = createSlice({
  name: "state",
  initialState,
  reducers: {
    setStateName: (state, action: PayloadAction<string>) => {
      state.formData.state_name = action.payload;
    },
    setStateStatus: (state, action: PayloadAction<string>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { state_name: "", status: "active" };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload.records.map((record: any) => ({
          id: record.stateId,
          stateId:record.stateId,
          state_name: record.stateName,
          user_name: record.userName,
          status: record.status,
          updated_at: record.updatedOn,
          updated_by: record.updatedBy,
          action: record.action,
        }));
        state.pagination = action.payload.pagination;
        console.log("States fetched successfully:", action.payload);
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createState.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          const newState: State = {
            id: action.payload.stateId,
            stateId:action.payload.stateId,
            state_name: action.payload.stateName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action: action.payload.action,
          };
          state.states.unshift(newState); 
          }
        // state.states.push(action.payload);
        state.formData = { state_name: "", status: "active" };
      })
      .addCase(createState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editState.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if(action.payload) {
          const updatedState: State = {
            id: action.payload.stateId,
            stateId:action.payload.stateId,
            state_name: action.payload.stateName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action: action.payload.action,
          };
          action.payload = updatedState;
        
          const index = state.states.findIndex(s => s.id === updatedState.id);
          if (index !== -1) {
            state.states[index] = updatedState;
          }
        }
        state.formData = { state_name: "", status: "active" };
      })
      .addCase(editState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

       // Toggle Status
      .addCase(toggleStateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleStateStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedState : State = {
          id: action.payload.stateId,
          stateId:action.payload.stateId,
          state_name: action.payload.stateName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.states.findIndex(s => s.id === updatedState.id);
        if (index !== -1) {
          state.states[index] = updatedState;
        }
      })
      .addCase(toggleStateStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
  },
});

export const { setStateName, setStateStatus, resetForm } = stateSlice.actions;
// ✅ Selectors
export const selectStates = (state: RootState) => state.state.states;
export const selectStateLoading = (state: RootState) => state.state.loading;
export const selectStateError = (state: RootState) => state.state.error;
export const selectStatePagination = (state: RootState) => state.state.pagination

export default stateSlice.reducer;
